import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  BookOpen,
  Send,
  Eye,
  Menu,
  X
} from "lucide-react";
import {
  fetchAttemptTime,
  fetchQuizQuestions,
  saveQuizAnswer,
  submitQuiz,
  updateQuizTime,
} from "../../Services/quizeServices";
import { useLocation, useNavigate } from "react-router-dom";
import QuizLoadingSkeleton from "./loading/QuetionLoadingSkeleton";

// Enhanced Timer Component
const Timer = ({ initialMinutes = 10, onTimeComplete, quizId, attemptId, isReviewMode }) => {
  const [timeRemaining, setTimeRemaining] = useState(initialMinutes * 60);
  const [isRunning, setIsRunning] = useState(true);
  const lastFiveMinuteMark = useRef(null);

  const timeUpdate = async () => {
    try {
      await updateQuizTime(timeRemaining / 60, attemptId, quizId);
    } catch (err) {
      console.log('Error updating time:', err);
    }
  };

  useEffect(() => {
    if (isReviewMode) {
      setIsRunning(false);
      return;
    }

    let timerInterval;
    if (isRunning && timeRemaining > 0) {
      timerInterval = setInterval(() => {
        setTimeRemaining((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      if (onTimeComplete && typeof onTimeComplete === 'function') {
        onTimeComplete();
      }
    }

    return () => clearInterval(timerInterval);
  }, [isRunning, timeRemaining, onTimeComplete, isReviewMode]);

  useEffect(() => {
    if (
      timeRemaining > 0 &&
      timeRemaining % 60 === 0 &&
      lastFiveMinuteMark.current !== timeRemaining
    ) {
      lastFiveMinuteMark.current = timeRemaining;
      if (timeUpdate && typeof timeUpdate === 'function') {
        timeUpdate();
      }
    }
  }, [timeRemaining]);

  const formatTime = () => {
    const hours = Math.floor(timeRemaining / 3600);
    const minutes = Math.floor((timeRemaining % 3600) / 60);
    const seconds = timeRemaining % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeRemaining <= 60) return 'text-red-500 bg-red-50 border-red-200';
    if (timeRemaining <= 5 * 60) return 'text-orange-500 bg-orange-50 border-orange-200';
    if (timeRemaining <= 10 * 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getProgressPercentage = () => {
    return ((initialMinutes * 60 - timeRemaining) / (initialMinutes * 60)) * 100;
  };

  if (isReviewMode) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-center space-x-2 text-blue-700">
          <Eye className="w-4 h-4" />
          <span className="font-medium">Review Mode</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`border-2 rounded-lg p-4 shadow-sm transition-all duration-300 ${getTimerColor()}`}>
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Clock className="w-5 h-5" />
          <span className="text-sm font-medium">Time Remaining</span>
        </div>
        <div className="text-2xl font-bold mb-2">
          {formatTime()}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-current h-2 rounded-full transition-all duration-1000"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
        {timeRemaining <= 5 * 60 && (
          <div className="text-xs mt-2 font-medium animate-pulse">
            ⚠️ Less than 5 minutes remaining!
          </div>
        )}
      </div>
    </div>
  );
};

// Question Navigation Sidebar
const QuestionNavigationSidebar = ({ 
  totalQuestions, 
  currentPage, 
  answeredQuestions, 
  onQuestionSelect,
  isOpen,
  onToggle
}) => {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 w-80 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-full lg:h-auto
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <BookOpen className="w-5 h-5 mr-2" />
            Question Navigator
          </h3>
          <button
            onClick={onToggle}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Overview */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="text-sm text-gray-600 mb-2">
            Progress: {answeredQuestions.size} of {totalQuestions} completed
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(answeredQuestions.size / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: totalQuestions }, (_, index) => {
              const questionNumber = index + 1;
              const isAnswered = answeredQuestions.has(questionNumber);
              const isCurrent = currentPage === questionNumber;
              
              return (
                <button
                  key={questionNumber}
                  onClick={() => onQuestionSelect(questionNumber)}
                  className={`
                    h-12 w-12 rounded-lg border-2 transition-all duration-200 text-sm font-medium
                    ${isCurrent 
                      ? 'border-blue-500 bg-blue-500 text-white shadow-md' 
                      : isAnswered
                        ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                    }
                  `}
                >
                  {questionNumber}
                  {isAnswered && !isCurrent && (
                    <CheckCircle className="w-3 h-3 ml-1 inline" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600 space-y-2">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-500 mr-2"></div>
              <span>Current Question</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-50 mr-2"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded border-2 border-gray-300 bg-white mr-2"></div>
              <span>Unanswered</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function QuizeAttempt() {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const quizId = queryParams.get("quize_id");
  const attemptId = queryParams.get("attempt_id");
  const action = queryParams.get("action");

  // Get current page from URL or default to 1
  const initialPage = parseInt(queryParams.get("page")) || 1;

  const [questionsData, setQuestionsData] = useState(null);
  const [attemptData, setAttemptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isQuetionLoading, setIsQuestionLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
  const [isSubmiting, setIsSubmiting] = useState(false);
  // Update URL when page changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("page", currentPage.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [currentPage, location.pathname, location.search, navigate]);

  // Check mode
  useEffect(() => {
    if (action === "review") {
      setIsReviewMode(true);
    }
  }, [action]);

  // Fetch questions on component mount and when page changes
  useEffect(() => {
    const getQuestions = async () => {
      try {
        setIsQuestionLoading(true);
        const data = await fetchQuizQuestions(
          quizId,
          attemptId,
          currentPage,
          pageSize
        );
        setQuestionsData(data);

        // Set previously selected answers from the API response
        if (data && data.questions && data.questions.length > 0) {
          const currentQuestion = data.questions[0];

          // Find any answer marked as isSelected: true
          const selectedAnswer = currentQuestion.answers.find(
            (answer) => answer.isSelected === true
          );

          if (selectedAnswer) {
            setSelectedAnswers((prev) => ({
              ...prev,
              [currentQuestion.question_id]: selectedAnswer.answer_id,
            }));
            
            // Mark question as answered
            setAnsweredQuestions(prev => new Set([...prev, currentPage]));
          }
        }

        setError(null);
      } catch (err) {
        setError("Failed to load quiz questions. Please try again later.");
      } finally {
        setLoading(false);
        setIsQuestionLoading(false);
      }
    };

    getQuestions();
  }, [quizId, attemptId, currentPage, pageSize]);

  useEffect(() => {
    const getAttemptData = async () => {
      try {
        const data = await fetchAttemptTime(quizId, attemptId);
        setAttemptData(data);
        if (
          data?.isCompleted === 1 &&
          (action === "continue" || action === "start")
        ) {
          alert("You have already submitted this quiz");
          setIsReviewMode(true);
        }
      } catch (err) {
        setError("Failed to load quiz attempt data. Please refresh the page");
      }
    };
    getAttemptData();
  }, [quizId, attemptId, action]);

  // Handle answer selection
  const handleAnswerSelect = async (questionId, answerId) => {
    if (isReviewMode) return; // Disable selection in review mode
    
    // First update local state for immediate UI feedback
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answerId,
    }));

    // Mark question as answered
    setAnsweredQuestions(prev => new Set([...prev, currentPage]));

    // Clear any previous error
    setSaveError(null);

    // Then save the answer to the server
    try {
      await saveQuizAnswer(attemptId, questionId, answerId);
      console.log("Answer saved successfully");
    } catch (error) {
      console.error("Failed to save answer:", error);
      setSaveError("Failed to save your answer. Please try again.");
    }
  };

  // Handle question navigation
  const handleQuestionSelect = (questionNumber) => {
    setCurrentPage(questionNumber);
    setSidebarOpen(false);
  };

  // Handle pagination
  const handleNextPage = () => {
    if (questionsData?.pagination?.hasMore) {
      setCurrentPage((prev) => prev + 1);
      setIsQuestionLoading(true);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setIsQuestionLoading(true);
    }
  };

  const handleSubmit = async () => {
    setIsSubmiting(true);
    try {
      const data = await submitQuiz(attemptId, quizId);
      if (data) {
        console.log("submit data", data);
        navigate(`/quize?quiz_id=${quizId}`);
      }
    } catch (err) {
      console.log("Error submitting quiz:", err);
      setSaveError("Failed to submit your quiz. Please try again.");
    }finally{
      setIsSubmiting(false);
    }
  };

  const handleTimeComplete = () => {
    alert("Time's up! Submitting your quiz automatically.");
    handleSubmit();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 p-6 rounded-lg border border-red-200 max-w-md w-full">
          <div className="flex items-center text-red-600">
            <AlertTriangle className="w-6 h-6 mr-3" />
            <div>
              <h3 className="font-semibold">Error</h3>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    !questionsData ||
    !questionsData.questions ||
    questionsData.questions.length === 0
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 max-w-md w-full">
          <div className="flex items-center text-yellow-700">
            <AlertTriangle className="w-6 h-6 mr-3" />
            <div>
              <h3 className="font-semibold">No Questions</h3>
              <p className="text-sm">No questions available for this quiz.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { questions, pagination } = questionsData;
  const currentQuestion = questions[0]; // Since we're using pageSize=1

  return (
    <div className="min-h-screen min-w-full bg-gray-50">
      <div className="flex h-screen">
        {/* Question Navigation Sidebar */}
        <QuestionNavigationSidebar
          totalQuestions={pagination?.totalCount || 0}
          currentPage={currentPage}
          answeredQuestions={answeredQuestions}
          onQuestionSelect={handleQuestionSelect}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header */}
          <div className="bg-white border-b border-gray-200 p-2 sm:p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <h1 className="text-xl font-semibold text-gray-800">
                  Quiz Attempt
                </h1>
              </div>
              <div className="w-full sm:w-64">
                <Timer
                  initialMinutes={attemptData?.timeReaming}
                  quizId={quizId}
                  attemptId={attemptId}
                  onTimeComplete={handleTimeComplete}
                  isReviewMode={isReviewMode}
                />
              </div>
            </div>
          </div>

          {/* Question Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="w-full mx-auto p-2 sm:p-6">
              {/* Question Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Question Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">
                      Question {pagination.currentPage} of {pagination.totalCount}
                    </h2>
                    {isReviewMode && (
                      <span className="bg-blue-500 px-3 py-1 rounded-full text-sm font-medium">
                        Review Mode
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-blue-500 rounded-full h-2 mt-3">
                    <div
                      className="bg-white h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(pagination.currentPage / pagination.totalCount) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Question Body */}
                {isQuetionLoading ? (
                  <QuizLoadingSkeleton />
                ) : (
                  <div className="p-6">
                    <div className="mb-6">
                      <h3 className="text-xl font-medium text-gray-800 mb-4 leading-relaxed">
                        {currentQuestion.question_text}
                      </h3>
                      {currentQuestion.question_img && (
                        <div className="mb-4">
                          <img
                            src={currentQuestion.question_img}
                            alt="Question"
                            className="w-full max-h-64 object-contain rounded-lg border border-gray-200 shadow-sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* Answer Options */}
                    <div className="space-y-3">
                      {currentQuestion.answers.map((answer, index) => {
                        const isSelected =
                          selectedAnswers[currentQuestion.question_id] === answer.answer_id;
                        const showCorrectness = isReviewMode && (isSelected || answer.is_correct);
                        
                        let answerClasses = "border-2 rounded-lg p-4 transition-all duration-200 cursor-pointer";
                        
                        if (isReviewMode) {
                          if (answer.is_correct) {
                            answerClasses += " border-green-500 bg-green-50";
                          } else if (isSelected && !answer.is_correct) {
                            answerClasses += " border-red-500 bg-red-50";
                          } else {
                            answerClasses += " border-gray-200 bg-gray-50";
                          }
                        } else if (isSelected) {
                          answerClasses += " border-blue-500 bg-blue-50 shadow-sm";
                        } else {
                          answerClasses += " border-gray-200 hover:border-blue-300 hover:bg-blue-50";
                        }

                        return (
                          <div
                            key={answer.answer_id}
                            className={answerClasses}
                            onClick={() => handleAnswerSelect(currentQuestion.question_id, answer.answer_id)}
                          >
                            <div className="flex items-center">
                              <div className="flex items-center justify-center w-8 h-8 mr-4 flex-shrink-0">
                                <div
                                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                                    isSelected
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {isSelected && (
                                    <div className="w-full h-full rounded-full bg-white transform scale-50" />
                                  )}
                                </div>
                                <span className="absolute text-sm font-medium text-gray-500 -ml-1">
                                  {/* {String.fromCharCode(65 + index)} */}
                                </span>
                              </div>
                              <div className="flex-grow">
                                <p className="text-gray-800 font-medium">
                                  {answer.answer_text}
                                </p>
                                {answer.answer_img && (
                                  <img
                                    src={answer.answer_img}
                                    alt="Answer option"
                                    className="mt-3 max-h-32 object-contain rounded border border-gray-200"
                                  />
                                )}
                              </div>
                              {showCorrectness && (
                                <div className="ml-4">
                                  {answer.is_correct ? (
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                  ) : isSelected ? (
                                    <XCircle className="w-6 h-6 text-red-500" />
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {saveError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center text-red-600">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    <span>{saveError}</span>
                  </div>
                </div>
              )}

              {/* Navigation */}
              {!isQuetionLoading && (
                <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-4">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`flex items-center px-2 sm:px-6 py-2 rounded-lg transition-all ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-300"
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous
                    </button>

                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                      <span>Question</span>
                      <span className="font-semibold">{currentPage}</span>
                      <span>of</span>
                      <span className="font-semibold">{pagination?.totalCount || 0}</span>
                    </div>

                    {pagination?.hasMore ? (
                      <button
                        onClick={handleNextPage}
                        className="flex items-center px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isReviewMode}
                        className={`flex items-center px-6 py-2 rounded-lg transition-all ${
                          isReviewMode
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700"
                        }`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {!isSubmiting ? "Submit" : "processing"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}